import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

/**
 * Renders the datepicker popper in document.body so it is not clipped by modal overflow.
 * Wraps children in DatePickerGlobalStyles so themed calendar styles still apply.
 */
export const renderDatePickerPopper = ({ children }: { children: ReactNode }) => {
  if (typeof document === 'undefined') {
    return <>{children}</>;
  }

  return createPortal(<DatePickerGlobalStyles>{children}</DatePickerGlobalStyles>, document.body);
};

/**
 * Global styles for react-datepicker popper (calendar + month/year dropdowns).
 * Shared by form date inputs and standalone pickers (e.g. SST date range).
 */
export const DatePickerGlobalStyles = styled.div`
  .react-datepicker-popper {
    z-index: ${(p) => p.theme.zIndex.xxl};
  }

  .react-datepicker {
    border: 1px solid ${(p) => p.theme.colors.border.pya};
    border-radius: ${(p) => p.theme.radius.lg};
    box-shadow: ${(p) => p.theme.shadows.md};
    font-family: ${(p) => p.theme.font.family};
  }

  .react-datepicker__header {
    background-color: ${(p) => p.theme.colors.grey[5]};
    border-bottom: 1px solid ${(p) => p.theme.colors.border.pya};
    border-radius: ${(p) => p.theme.radius.lg} ${(p) => p.theme.radius.lg} 0 0;
    padding: ${(p) => p.theme.spacing.sm};
  }

  .react-datepicker__current-month {
    font-size: ${(p) => p.theme.font.size.sm};
    font-weight: ${(p) => p.theme.font.weight.medium};
    color: ${(p) => p.theme.colors.grey[70]};
  }

  .react-datepicker__day-name,
  .react-datepicker__day {
    color: ${(p) => p.theme.colors.general.text};
    font-size: ${(p) => p.theme.font.size.xs};
    margin: 2px;
    width: 30px;
    height: 30px;
    line-height: 28px;
    border-radius: ${(p) => p.theme.radius.sm};
  }

  .react-datepicker__day:hover {
    background-color: ${(p) => p.theme.colors.grey[10]};
    color: ${(p) => p.theme.colors.general.link};
  }

  .react-datepicker__day--selected {
    background-color: ${(p) => p.theme.colors.general.link};
    color: white;
  }

  .react-datepicker__day--selected:hover {
    background-color: ${(p) => p.theme.colors.general.primary};
  }

  .react-datepicker__day--today {
    background-color: ${(p) => p.theme.colors.grey[10]};
    color: ${(p) => p.theme.colors.general.link};
    font-weight: ${(p) => p.theme.font.weight.medium};
  }

  .react-datepicker__day--outside-month {
    color: ${(p) => p.theme.colors.general.placeholder};
  }

  .react-datepicker__navigation {
    background: none;
    border: none;
    outline: none;
  }

  .react-datepicker__navigation-icon::before {
    border-color: ${(p) => p.theme.colors.grey[50]};
  }

  .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
    border-color: ${(p) => p.theme.colors.general.link};
  }

  .react-datepicker__month-dropdown,
  .react-datepicker__year-dropdown {
    background-color: ${(p) => p.theme.colors.general.white};
    border: 1px solid ${(p) => p.theme.colors.border.pya};
    border-radius: ${(p) => p.theme.radius.lg};
    box-shadow: ${(p) => p.theme.shadows.md};
  }

  .react-datepicker__month-option,
  .react-datepicker__year-option {
    color: ${(p) => p.theme.colors.general.text};
    padding: ${(p) => p.theme.spacing.xs} ${(p) => p.theme.spacing.sm};
    font-size: ${(p) => p.theme.font.size.sm};
  }

  .react-datepicker__month-option:hover,
  .react-datepicker__year-option:hover {
    background-color: ${(p) => p.theme.colors.grey[10]};
    color: ${(p) => p.theme.colors.general.link};
  }

  .react-datepicker__month-option--selected,
  .react-datepicker__year-option--selected {
    background-color: ${(p) => p.theme.colors.general.link};
    color: white;
  }

  .react-datepicker__close-icon {
    cursor: pointer;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    background: none;
    border: none;
    outline: none;
    padding: 0;

    &:hover {
      opacity: 0.7;
    }

    &::after {
      content: '✕';
      color: ${(p) => p.theme.colors.grey[60]};
      background: none;
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      display: block;
      text-align: center;
    }
  }
`;
